import React, { useState } from 'react';
import {
  Palette,
  Layout,
  Smartphone,
  Search,
  Layers,
  Figma,
  MousePointerClick,
  TestTube2,
  Code2,
  CheckCircle2,
  ArrowRight,
  Eye,
  Sliders,
  Sparkle,
  MessageSquare,
  Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const UIUXDesign = () => {
  const [activeDesignStep, setActiveDesignStep] = useState(0);

  const services = [
    {
      icon: Search,
      title: 'UX Research & Journey Mapping',
      color: 'from-cyan-500 to-blue-600',
      description: 'Deep stakeholder discovery, user persona synthesis, empathy mapping, and friction-free user journey workflows.',
      bullets: ['User Persona Workshops', 'Competitor Benchmarking', 'User Flow & Sitemap Architecture']
    },
    {
      icon: Layout,
      title: 'Wireframing & Information Architecture',
      color: 'from-blue-500 to-indigo-600',
      description: 'Rapid low-fidelity wireframing to validate layout hierarchy, content prioritization, and intuitive navigation loops.',
      bullets: ['Low-Fi & Mid-Fi Wireframes', 'Interactive Layout Testing', 'Sitemap Structural Specs']
    },
    {
      icon: Palette,
      title: 'UI Design & Visual Systems',
      color: 'from-orange-500 to-amber-500',
      description: 'High-fidelity pixel-perfect UI screens built with harmonious color palettes, modern typography, and glassmorphism accents.',
      bullets: ['Figma Auto-Layout Components', 'Dark & Light Mode Systems', 'Brand Style Guides & Tokens']
    },
    {
      icon: MousePointerClick,
      title: 'Interactive Prototyping & Motion',
      color: 'from-purple-500 to-pink-600',
      description: 'High-fidelity clickable prototypes featuring realistic micro-interactions, smooth hover states, and gesture controls.',
      bullets: ['Clickable Figma Prototypes', 'Micro-Animation & Lottie Integration', 'Mobile Gesture Interactions']
    },
    {
      icon: TestTube2,
      title: 'Usability Testing & CRO Audits',
      color: 'from-emerald-500 to-teal-600',
      description: 'Rigorous end-user testing, heatmap analysis, and Conversion Rate Optimization (CRO) to eliminate UX drop-off bottlenecks.',
      bullets: ['A/B Testing & Heatmap Audits', 'Task Completion Velocity', 'Accessibility (WCAG 2.1) Audits']
    },
    {
      icon: Code2,
      title: 'Developer Handoff & QA Handoff',
      color: 'from-pink-500 to-rose-600',
      description: 'Zero-friction developer handoff with CSS design tokens, exportable SVG assets, and pixel-perfect design QA reviews.',
      bullets: ['Figma Tokens & CSS Export', 'Storybook Component Sync', 'Design-to-Code QA Audits']
    }
  ];

  const designProcess = [
    {
      step: '01',
      code: 'DISCOVERY_RESEARCH',
      phase: 'Discovery & Research',
      icon: Search,
      title: 'Discovery & Research',
      badge: 'Sprint 0 Research',
      description: 'Exhaustive initial scoping: understand business goals, conduct end-user interviews, run competitor analysis, gather technical requirements, and audit market dynamics.',
      image: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&q=80',
      tasks: ['Understand business goals', 'User research', 'Competitor analysis', 'Requirement gathering', 'Market research'],
      deliverables: ['Project brief', 'User personas', 'Research report'],
      primaryBtnText: 'Explore Research Methodology',
      primaryBtnLink: '/about-us',
      secondaryBtnText: 'Talk to UX Researchers',
      secondaryBtnLink: '/contact'
    },
    {
      step: '02',
      code: 'STRATEGY_PLANNING',
      phase: 'Strategy & Planning',
      icon: Layout,
      title: 'Strategy & Planning',
      badge: 'Product Strategy',
      description: 'Structuring the product foundation: product roadmap blueprinting, information architecture design, user journey mapping, feature backlog prioritization, and technical stack planning.',
      image: 'https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?w=800&q=80',
      tasks: ['Product roadmap', 'Information architecture', 'User journey mapping', 'Feature prioritization', 'Technical planning'],
      deliverables: ['Sitemap', 'User flows', 'Product strategy'],
      primaryBtnText: 'Explore Strategy Blueprint',
      primaryBtnLink: '/enterprise-solutions',
      secondaryBtnText: 'Talk to Product Strategists',
      secondaryBtnLink: '/contact'
    },
    {
      step: '03',
      code: 'UX_DESIGN',
      phase: 'UX Design',
      icon: Eye,
      title: 'UX Design & Wireframing',
      badge: 'UX Architecture',
      description: 'Crafting user-centered wireframes and low-fidelity prototypes to plan seamless navigation structures, screen hierarchies, and user flow efficiency.',
      image: 'https://images.unsplash.com/photo-1542744094-3a3172720177?w=800&q=80',
      tasks: ['Wireframes', 'Low-fidelity prototypes', 'User experience planning', 'Navigation structure'],
      deliverables: ['Wireframes', 'User flow diagrams'],
      primaryBtnText: 'Explore Wireframing Specs',
      primaryBtnLink: '/ui-ux-design',
      secondaryBtnText: 'Talk to UX Architects',
      secondaryBtnLink: '/contact'
    },
    {
      step: '04',
      code: 'UI_DESIGN_SYSTEM',
      phase: 'UI Design & Design System',
      icon: Palette,
      title: 'UI Design & System Architecture',
      badge: 'Figma UI Tokens',
      description: 'Creating high-definition visual interfaces, building scalable Figma design systems, curated color palettes, typography rules, responsive layouts, and clickable prototypes.',
      image: '/images/ecom_single_product_view_mockup.png',
      tasks: ['Visual design', 'Design system', 'Color palette', 'Typography', 'Responsive layouts', 'Interactive prototypes'],
      deliverables: ['High-fidelity mockups', 'Design system', 'Clickable prototype'],
      primaryBtnText: 'Explore Figma Design System',
      primaryBtnLink: '/ui-ux-design',
      secondaryBtnText: 'Talk to UI Designers',
      secondaryBtnLink: '/contact'
    },
    {
      step: '05',
      code: 'FULLSTACK_DEV',
      phase: 'Development & Engineering',
      icon: Code2,
      title: 'Full-Stack Development',
      badge: 'Clean Code Sprints',
      description: 'Translating designs into high-performance code: frontend React/Next.js development, backend microservices, REST/GraphQL API integration, database architecture, and CMS integration.',
      image: '/images/ecom_multi_product_catalog_mockup.png',
      tasks: ['Frontend development', 'Backend development', 'API integration', 'Database development', 'CMS integration'],
      deliverables: ['Functional application', 'Source code'],
      primaryBtnText: 'Explore Full-Stack Engineering',
      primaryBtnLink: '/web-development',
      secondaryBtnText: 'Talk to Lead Developers',
      secondaryBtnLink: '/contact'
    },
    {
      step: '06',
      code: 'TESTING_QA',
      phase: 'Testing & Quality Assurance',
      icon: TestTube2,
      title: 'Testing & Quality Assurance',
      badge: 'Zero-Defect Audit',
      description: 'Comprehensive QA validation: functional testing, visual UI auditing, cross-browser compatibility, load & performance tuning, and OWASP penetration security testing.',
      image: '/images/ecom_shopping_cart_checkout_mockup.png',
      tasks: ['Functional testing', 'UI testing', 'Cross-browser testing', 'Performance testing', 'Security testing'],
      deliverables: ['Bug reports', 'QA approval'],
      primaryBtnText: 'Explore QA & Security Services',
      primaryBtnLink: '/qa-testing',
      secondaryBtnText: 'Talk to Security Engineers',
      secondaryBtnLink: '/contact'
    },
    {
      step: '07',
      code: 'LAUNCH_SUPPORT',
      phase: 'Launch & Support',
      icon: Rocket,
      title: 'Launch & Continuous Support',
      badge: '24/7 Managed Cloud',
      description: 'Production cloud deployment, 24/7 telemetry performance monitoring, SLA maintenance, feature updates, and continuous product improvement.',
      image: '/images/ecom_analytics_dashboard_mockup.png',
      tasks: ['Deployment', 'Performance monitoring', 'Maintenance', 'Feature updates', 'Continuous improvement'],
      deliverables: ['Live product', 'Support documentation'],
      primaryBtnText: 'Explore Cloud Launch & SLA',
      primaryBtnLink: '/cloud-devops',
      secondaryBtnText: 'Talk to DevOps Architects',
      secondaryBtnLink: '/contact'
    }
  ];

  const toolsStack = [
    { name: 'Figma', category: 'UI/UX & Systems' },
    { name: 'Adobe XD', category: 'Prototyping' },
    { name: 'Framer', category: 'Interactive Web' },
    { name: 'Principle / ProtoPie', category: 'Micro-Animations' },
    { name: 'LottieFiles', category: 'Motion Assets' },
    { name: 'Storybook', category: 'Component Docs' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-left pt-20">

      {/* Hero Section with Digital UI/UX Background Image */}
      <section className="py-24 bg-gray-950 relative overflow-hidden border-b border-gray-800/80">
        {/* Background Digital UI/UX Graphic & Dark Ambient Overlays */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/uiux_hero_bg_mockup.png"
            alt="UI UX Wireframe Design Tokens Background"
            className="w-full h-full object-cover filter brightness-90 contrast-110 opacity-45 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/90 via-gray-950/70 to-gray-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950/95 via-transparent to-gray-950/95" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">

            <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-6">
              <Palette className="w-4 h-4 text-orange-400" />
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Human-Centered Product Design
              </span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              Designing Intuitive, <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Conversion-Focused</span> Digital Products
            </h1>

            <p className="text-gray-300 text-base md:text-xl leading-relaxed mb-10 max-w-3xl mx-auto">
              We blend deep user research with cutting-edge visual design systems to transform complex web and mobile ideas into effortless, delightful user experiences.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a href="/contact">
                <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                  Request Design Audit
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </a>
              <a href="#process">
                <Button variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800 px-8 py-6 text-base font-semibold rounded-xl">
                  Explore UI/UX Process
                </Button>
              </a>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Design Services Grid */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                Full-Spectrum Design Capabilities
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Our UI/UX <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Design Solutions</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              From early concept wireframes to scalable Figma component libraries, we cover every stage of product design.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div
                key={idx}
                className="group bg-gray-900/90 border border-gray-800 hover:border-cyan-500/70 rounded-2xl p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-cyan-500/10 flex flex-col justify-between"
              >
                <div>
                  <div className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <service.icon className="w-7 h-7 text-white" />
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-gray-300 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-800/80 space-y-2">
                  {service.bullets.map((bullet, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-300 font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5 text-orange-400 mr-2 flex-shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Interactive 5-Step Product Design Process - Flat Light Theme */}
      <section id="process" className="py-24 bg-slate-50 border-t border-b border-gray-200 relative overflow-hidden text-left">
        {/* Background Ambient Orbs */}
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-gradient-to-br from-orange-200/40 via-amber-200/40 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" />
        <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-gradient-to-tr from-cyan-200/40 via-blue-200/40 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" />

        <div className="container mx-auto px-6 relative z-10">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-orange-100/90 border border-orange-200 rounded-full px-4 py-1.5 mb-4 shadow-sm">
              <span className="text-orange-800 text-sm font-mono font-bold uppercase tracking-wider">
                HUMAN-CENTERED DESIGN METHODOLOGY
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
              Our 7-Step <span className="bg-gradient-to-r from-orange-600 via-amber-500 to-cyan-600 bg-clip-text text-transparent">Product Design & Engineering Process</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              A structured 7-stage human-centric design & full-stack development roadmap that turns raw concepts into market-validated, high-converting digital platforms.
            </p>
          </div>

          {/* Interactive 7-Box Horizontal Timeline Strip in Single Row */}
          <div className="mb-10 relative">
            {/* Horizontal Timeline Connecting Line Strip */}
            <div className="absolute top-1/2 left-4 right-4 h-1 bg-gradient-to-r from-orange-400 via-amber-400 via-emerald-400 to-cyan-500 -translate-y-1/2 hidden lg:block z-0" />

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 relative z-10">
              {designProcess.map((stageItem, idx) => (
                <div
                  key={idx}
                  onClick={() => setActiveDesignStep(idx)}
                  className={`cursor-pointer rounded-2xl p-3.5 text-center transition-all duration-300 relative flex flex-col justify-between ${activeDesignStep === idx
                    ? 'bg-white border-2 border-orange-500 shadow-xl shadow-orange-500/15 ring-2 ring-orange-500/20 scale-105 z-20'
                    : 'bg-white/95 border border-gray-200 hover:border-orange-300 hover:shadow-md opacity-85 hover:opacity-100 z-10'
                    }`}
                >
                  {/* Top Timeline Node Marker */}
                  <div className="flex items-center justify-between mb-2">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center font-mono font-black text-sm transition-all ${activeDesignStep === idx
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700'
                      }`}>
                      {stageItem.step}
                    </span>
                    <span className={`w-2.5 h-2.5 rounded-full ${activeDesignStep === idx ? 'bg-orange-500 animate-ping' : 'bg-gray-300'
                      }`} />
                  </div>

                  {/* Phase Title Only (No Duplicates) */}
                  <div className="text-left my-1">
                    <div className="text-sm font-extrabold text-gray-900 line-clamp-2 leading-tight">
                      {stageItem.phase}
                    </div>
                  </div>

                  {/* Bottom Phase Indicator */}
                  <div className="mt-2 text-left pt-2 border-t border-gray-100">
                    <span className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-md inline-block ${activeDesignStep === idx
                      ? 'bg-orange-100 text-orange-800 border border-orange-200'
                      : 'bg-slate-100 text-gray-600'
                      }`}>
                      Phase {stageItem.step}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active Step Interactive Console Card */}
          <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-xl mb-12 relative overflow-hidden">

            {/* Header Badge Strip */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <span className="text-3xl font-black font-mono text-orange-600">STEP {designProcess[activeDesignStep].step}</span>
                <span className="text-sm font-mono text-cyan-800 bg-cyan-100/80 border border-cyan-200 px-3 py-1 rounded-full font-bold">
                  {designProcess[activeDesignStep].code}
                </span>
              </div>
              <span className="text-sm font-mono text-emerald-800 bg-emerald-100/80 border border-emerald-200 px-3.5 py-1.5 rounded-full font-semibold uppercase">
                PHASE {designProcess[activeDesignStep].step}: {designProcess[activeDesignStep].phase}
              </span>
            </div>

            <div className="grid lg:grid-cols-12 gap-8 items-center">

              {/* Left Column: Title, Description, Tasks & Deliverables & Buttons */}
              <div className="lg:col-span-7 space-y-5">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                  {designProcess[activeDesignStep].title}
                </h3>

                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  {designProcess[activeDesignStep].description}
                </p>

                {/* Key Phase Tasks */}
                <div className="pt-1">
                  <div className="text-sm text-gray-700 font-bold mb-2 uppercase tracking-wider">Phase Key Tasks:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {designProcess[activeDesignStep].tasks.map((taskItem, tkIdx) => (
                      <span key={tkIdx} className="bg-orange-50 border border-orange-200 text-orange-900 text-[11px] font-mono font-bold px-2.5 py-1 rounded-lg shadow-xs">
                        ✓ {taskItem}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Key Phase Deliverables */}
                <div>
                  <div className="text-sm text-cyan-800 font-bold mb-2 uppercase tracking-wider">Phase Deliverables:</div>
                  <div className="flex flex-wrap gap-2">
                    {designProcess[activeDesignStep].deliverables.map((tag, tIdx) => (
                      <span key={tIdx} className="bg-slate-100 border border-gray-200 text-slate-800 text-[11px] font-mono font-bold px-3 py-1 rounded-lg shadow-xs">
                        📦 {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Enterprise Solutions & Talk to Expert Style Buttons */}
                <div className="bg-slate-50 border border-gray-200 p-1.5 rounded-2xl inline-flex flex-wrap gap-2 shadow-sm pt-2 mt-4">
                  <Link to={designProcess[activeDesignStep].primaryBtnLink}>
                    <button className="px-5 py-2.5 rounded-xl text-sm md:text-sm font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2">
                      <span>{designProcess[activeDesignStep].primaryBtnText}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>

                  <Link to={designProcess[activeDesignStep].secondaryBtnLink}>
                    <button className="px-5 py-2.5 rounded-xl text-sm md:text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-amber-200" />
                      <span>{designProcess[activeDesignStep].secondaryBtnText}</span>
                    </button>
                  </Link>
                </div>
              </div>

              {/* Right Column: Digital Figma UI Mockup & Milestones */}
              <div className="lg:col-span-5 bg-slate-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
                <div className="relative h-48 rounded-xl overflow-hidden mb-5 border border-gray-200">
                  <img
                    src={designProcess[activeDesignStep].image}
                    alt={designProcess[activeDesignStep].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2 bg-slate-900/90 backdrop-blur-md px-2.5 py-0.5 rounded text-[11px] font-mono text-cyan-400 font-bold border border-slate-700">
                    DIGITAL PLATFORM MOCKUP
                  </div>
                </div>

                <div className="text-sm font-mono text-gray-600 uppercase tracking-wider mb-3 font-bold flex items-center justify-between">
                  <span>Deliverables Clearance</span>
                  <span className="text-emerald-700">100% Verified</span>
                </div>

                <div className="space-y-2">
                  {designProcess[activeDesignStep].deliverables.map((item, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-800 bg-white p-2.5 rounded-lg border border-gray-200 shadow-sm">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mr-2 flex-shrink-0" />
                      <span className="truncate font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Stepper Nav Controls */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                disabled={activeDesignStep === 0}
                onClick={() => setActiveDesignStep(prev => Math.max(0, prev - 1))}
                className={`text-sm font-mono font-bold px-4 py-2 rounded-xl border transition-all ${activeDesignStep === 0
                  ? 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'bg-slate-900 text-white hover:bg-orange-500 border border-slate-900 hover:border-orange-500 shadow-sm'
                  }`}
              >
                ← PREVIOUS STEP
              </button>

              <div className="flex space-x-1.5">
                {designProcess.map((_, i) => (
                  <div
                    key={i}
                    onClick={() => setActiveDesignStep(i)}
                    className={`w-2.5 h-2.5 rounded-full cursor-pointer transition-all ${activeDesignStep === i ? 'bg-orange-500 w-6' : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                  />
                ))}
              </div>

              <button
                disabled={activeDesignStep === designProcess.length - 1}
                onClick={() => setActiveDesignStep(prev => Math.min(designProcess.length - 1, prev + 1))}
                className={`text-sm font-mono font-bold px-4 py-2 rounded-xl border transition-all ${activeDesignStep === designProcess.length - 1
                  ? 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'bg-slate-900 text-white hover:bg-orange-500 border border-slate-900 hover:border-orange-500 shadow-sm'
                  }`}
              >
                NEXT STEP →
              </button>
            </div>

          </div>

        </div>
      </section>

      {/* Tools & Tech Stack */}
      <section className="py-20 bg-gray-900 border-t border-gray-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-3">
              Industry-Standard Design Toolchain
            </h2>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              We leverage modern design platforms to build collaborative, developer-friendly design assets.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {toolsStack.map((tool, idx) => (
              <div key={idx} className="bg-gray-950 border border-gray-800 rounded-2xl p-5 text-center hover:border-cyan-500/60 transition-all">
                <div className="text-base font-bold text-white mb-1">{tool.name}</div>
                <div className="text-[11px] text-gray-400 font-medium">{tool.category}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Box */}
      <section className="py-20 bg-gray-950">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <div className="bg-gradient-to-r from-slate-900 via-gray-900 to-slate-900 border border-orange-500/30 rounded-3xl p-10 md:p-14 shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Have a Product Idea in Mind? Let's Design It.
            </h2>
            <p className="text-gray-300 text-base mb-8 max-w-2xl mx-auto">
              Partner with our UX researchers and UI product designers to turn your software vision into a seamless, high-converting digital application.
            </p>
            <a href="/contact">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg">
                Schedule Design Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default UIUXDesign;
